import { useMemo } from "react";

import { BookingWizard } from "../../types";

export type CourseLayout = {
  id: string;
  name: string;
  holeCount: BookingWizard.HoleCount;
  code: string;
};

/**
 * Type representing the facility structure from the GraphQL schema
 */
type CourseLayoutNode = {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly enabled: boolean;
  readonly holeCount: BookingWizard.HoleCount;
  readonly memberVisible: boolean;
};

type FacilityWithCourseLayouts = {
  readonly courseLayouts?: {
    readonly edges: ReadonlyArray<{
      readonly node: CourseLayoutNode | null;
    } | null> | null;
  } | null;
};

/**
 * Hook to extract course layout options from a facility
 * @param facility The facility data containing course layouts from Relay fragment
 * @returns An array of course layouts sorted in reverse order
 */
export const useGetCourseLayoutOptions = (
  facility: FacilityWithCourseLayouts | null | undefined
): CourseLayout[] => {
  return useMemo((): CourseLayout[] => {
    if (!facility?.courseLayouts?.edges) return [];

    return facility.courseLayouts.edges
      .filter((edge) => edge?.node?.enabled && edge?.node?.memberVisible)
      .map((edge) => {
        const node = edge?.node;
        if (!node) return null;

        return {
          id: node.id,
          name: node.name,
          holeCount: node.holeCount,
          code: node.code,
        };
      })
      .filter((layout): layout is CourseLayout => layout !== null)
      .sort((a, b) => Number(a.code) - Number(b.code));
  }, [facility?.courseLayouts?.edges]);
};
